import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import RevengeInvitationModal from 'modules/Room/features/revenge-invitation-modal/revenge-invitation-modal';

import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { ROUTES } from 'shared/constants';
import { WinnerStatusEnum } from 'shared/constants/shared-enums';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';
import { useStartGameStore } from 'shared/store/start-game-store';

import ControllerIcon from 'assets/controller-brown.svg?react';
import defeatIcon from 'assets/png/final/defeat.png?format=webp;avif&as=picture';
import victoryIcon from 'assets/png/final/victory.png?format=webp;avif&as=picture';
import ShareIcon from 'assets/share.svg?react';
import RevengeIcon from 'assets/svg/revenge.svg?react';

interface INewFinishViewProps {
  isWinner: boolean;
  showModal?: boolean;
  onShareClick?: () => void;
  winnerStatus?: WinnerStatusEnum;
  amIWinner?: boolean;
  onRevengeClick?: () => void;
  onAcceptInvite?: (roomId: number) => void;
  invitedRoomId: number | null;
}
export const NewFinishView = ({
  isWinner,
  showModal,
  onShareClick,
  winnerStatus,
  amIWinner,
  onRevengeClick,
  onAcceptInvite,
  invitedRoomId,
}: INewFinishViewProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { address } = useAccount();
  const publicClient = usePublicClient();

  const { isStarting, isJoining, isApproving } = useStartGameStore();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { pointBalance, snapshotPoints, fetchUserBalances, setSnapshotPoints } = useQuestsStore();

  const [displayedPoints, setDisplayedPoints] = useState(Number(snapshotPoints));

  useEffect(() => {
    fetchUserBalances(publicClient, address);
  }, [publicClient, address]);

  useEffect(() => {
    if (pointBalance > snapshotPoints) {
      setDisplayedPoints(Number(snapshotPoints));

      const timeout = setTimeout(() => {
        setDisplayedPoints(Number(pointBalance));
      }, 2000);

      return () => clearTimeout(timeout);
    } else {
      setDisplayedPoints(Number(pointBalance));
    }
  }, [pointBalance, snapshotPoints]);

  useEffect(() => {
    if (invitedRoomId !== null) {
      setShowInviteModal(true);
    }
  }, [invitedRoomId]);

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <OptimizedImage
        imageData={isWinner ? victoryIcon : defeatIcon}
        alt={isWinner ? 'Victory' : 'Defeat'}
        className="mb-10 mt-5 w-[80%]"
      />
      {!isWinner && invitedRoomId && showInviteModal && (
        <RevengeInvitationModal
          setOpen={setShowInviteModal}
          invitedRoomId={invitedRoomId}
          onAcceptInvite={onAcceptInvite}
          className="w-[86%] max-w-[550px]"
          isLoading={isJoining || isApproving}
        />
      )}

      {isWinner && !amIWinner && (
        <div className="relative flex w-[86%] max-w-[550px] flex-col items-start justify-start gap-4 rounded-[20px] border border-solid border-border bg-violet p-5 xlg:gap-8">
          <div className="relative flex shrink-0 flex-col items-start justify-start gap-4 self-stretch">
            <div className="relative self-stretch text-left text-2xl font-semibold leading-8 text-white">
              {t('finishView.revengeTitle')}
            </div>
            <div className="relative self-stretch text-left text-base font-normal text-white">
              {t('finishView.revengeText')}
            </div>
          </div>
          <div className="mt-4 flex w-full items-center justify-between gap-4">
            <SecondaryBtn
              onClick={onRevengeClick}
              svg={<RevengeIcon className="h-5 w-5" />}
              className="w-full"
              childClassName="flex items-center justify-center"
              text="finishView.revenge"
              isLoading={isStarting || isApproving}
            />
          </div>
        </div>
      )}
      {showModal && (
        <div className="relative flex w-[86%] max-w-[550px] flex-col items-start justify-start gap-4 rounded-[20px] border border-solid border-border bg-violet p-5 xlg:gap-8">
          <div className="relative flex shrink-0 flex-col items-start justify-start gap-4 self-stretch">
            <div className="relative self-stretch text-left text-2xl font-semibold leading-8 text-white">
              {isWinner ? t('finishView.winTitle') : t('finishView.loseTitle')}
            </div>
            <div className="relative self-stretch text-left text-base font-normal text-white">
              {isWinner ? t('finishView.winText') : t('finishView.loseText')}
            </div>

            <div className="flex w-full items-center justify-center">
              <InfiniteMaxProgressBar
                value={Number(displayedPoints)}
                showAttention
                className="w-full max-w-64"
              />
            </div>
          </div>
          <div className="flex w-full items-center justify-between gap-3">
            <PrimaryButton
              onClick={() => {
                setSnapshotPoints();
                navigate(ROUTES.LOBBY);
              }}
              svg={<ControllerIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
              text={t('finishView.playAgain')}
              className="w-full"
              innerClassName="box-border flex items-center justify-center"
            />
            {winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
            winnerStatus === WinnerStatusEnum.WinnerPlayerB ? (
              <SecondaryBtn
                onClick={onShareClick}
                svg={<ShareIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
                childClassName="h-full box-border flex items-center justify-center sm:p-[13px]"
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
